/**
 * Round 115 — opt-in client-side SSE trace for the trial-chat surfaces.
 *
 * Loop 9 left the public-chat evidence drawer working in the component
 * test but missing in a real in-app browser. Component tests inject events
 * directly, so they can't see what the real transport actually delivers.
 * This trace logs every SSE event (and the post-stream finalize snapshot)
 * to the console so QA can read the true event sequence + final message
 * state in the real browser without a DevTools Network capture.
 *
 * It is OFF by default and never fires in production traffic. QA enables it
 * per-browser with:
 *
 *   localStorage.setItem('cea_chat_trace', '1')   // then reload
 *
 * or, for one session, `window.__CEA_CHAT_TRACE__ = true`.
 */

export function isChatTraceEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if ((window as unknown as { __CEA_CHAT_TRACE__?: boolean }).__CEA_CHAT_TRACE__) {
      return true;
    }
    if (window.localStorage?.getItem('cea_chat_trace') === '1') return true;
    const loc = window.location;
    // Round 115.1 #3 — Codex asked for the trace to be on by default in
    // local/QA so an automated browser can read the event log without poking
    // localStorage. Auto-enable on localhost + staging + an explicit
    // `?qa_chat_trace=1` query, NEVER on the production apex.
    if (loc?.search && /[?&]qa_chat_trace=1\b/.test(loc.search)) return true;
    const host = loc?.hostname ?? '';
    return (
      host === 'localhost' ||
      host === '127.0.0.1' ||
      host.endsWith('.local') ||
      host.startsWith('staging.')
    );
  } catch {
    return false;
  }
}

function summarizeEvent(ev: Record<string, unknown>): string {
  const type = String(ev.type ?? 'unknown');
  const parts: string[] = [`type=${type}`];
  if (typeof ev.text === 'string') parts.push(`text_len=${ev.text.length}`);
  if (typeof ev.name === 'string') parts.push(`name=${ev.name}`);
  for (const key of ['concepts', 'skills', 'vault_passages', 'external_sources']) {
    const value = ev[key];
    if (Array.isArray(value)) parts.push(`${key}=${value.length}`);
  }
  return parts.join(' ');
}

/** Log a single inbound SSE event when the trace flag is on. */
export function traceChatEvent(source: string, ev: Record<string, unknown>): void {
  if (!isChatTraceEnabled()) return;
  // console.warn (not debug) so it survives default console filters that
  // hide verbose/debug output in the in-app browser.
  console.warn(`[chat-trace:${source}] ${summarizeEvent(ev)}`, ev);
}

/** Log a labelled state snapshot (e.g. the post-stream finalize). */
export function traceChatState(
  source: string,
  label: string,
  detail: Record<string, unknown>,
): void {
  if (!isChatTraceEnabled()) return;
  const parts = Object.entries(detail).map(([k, v]) => `${k}=${String(v)}`);
  console.warn(`[chat-trace:${source}] ${label} ${parts.join(' ')}`);
}
